import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { usersApi, membersApi } from '@/lib/api';
import { Users, User, Plus, Key, Trash2, Shield, Edit, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'member',
    position: '',
    member_id: ''
  });
  
  const [newPassword, setNewPassword] = useState('');
  const [newPosition, setNewPosition] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersData, membersData] = await Promise.all([
        usersApi.getAll(),
        membersApi.getAll().catch(() => [])
      ]);
      setUsers(usersData);
      setMembers(membersData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetNewUserForm = () => {
    setNewUserData({
      name: '',
      email: '',
      password: '',
      role: 'member',
      position: '',
      member_id: ''
    });
  };

  const handleCreateUser = async () => {
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error('Please fill in name, email, and password');
      return;
    }
    
    if (newUserData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await usersApi.createAdmin(newUserData);
      toast.success('User account created');
      setCreateDialogOpen(false);
      resetNewUserForm();
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create user');
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await usersApi.updateRole(userId, role);
      toast.success('Role updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await usersApi.updatePassword(selectedUser.user_id, newPassword);
      toast.success('Password updated');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const handleUpdatePosition = async () => {
    try {
      await usersApi.updatePosition(selectedUser.user_id, newPosition);
      toast.success('Position updated');
      setPositionDialogOpen(false);
      setNewPosition('');
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update position');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user account? They will no longer be able to log in.')) return;
    
    try {
      await usersApi.delete(userId);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const openPositionDialog = (user) => {
    setSelectedUser(user);
    setNewPosition(user.position || '');
    setPositionDialogOpen(true);
  };

  // Auto-fill from member selection
  const handleMemberSelect = (memberId) => {
    if (memberId === 'none') {
      setNewUserData({ ...newUserData, member_id: '' });
      return;
    }
    
    const member = members.find(m => m.id === memberId);
    if (member) {
      setNewUserData({
        ...newUserData,
        member_id: memberId,
        name: member.name,
        email: member.email || newUserData.email
      });
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-700',
    member: 'bg-blue-100 text-blue-700',
    visitor: 'bg-stone-100 text-stone-700'
  };

  // Members without user accounts
  const availableMembers = members.filter(m => !m.user_id);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">User Management</h1>
          <p className="text-stone-600">Create admin accounts and manage user access</p>
        </div>
        
        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetNewUserForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-700 hover:bg-amber-800">
              <Plus className="h-4 w-4 mr-2" /> Create Account
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create User Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Link to existing member */}
              {availableMembers.length > 0 && (
                <div>
                  <Label>Link to Member (optional)</Label>
                  <Select value={newUserData.member_id || 'none'} onValueChange={handleMemberSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a member..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Don't link to member --</SelectItem>
                      {availableMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name} {member.email ? `(${member.email})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">Auto-fills name and email from member directory</p>
                </div>
              )}
              
              <div>
                <Label>Name *</Label>
                <Input
                  value={newUserData.name}
                  onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                />
              </div>
              
              <div>
                <Label>Role</Label>
                <Select value={newUserData.role} onValueChange={(v) => setNewUserData({ ...newUserData, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <span className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-red-600" /> Admin (Full access)
                      </span>
                    </SelectItem>
                    <SelectItem value="member">
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-600" /> Member (Directory access)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {newUserData.role === 'admin' && (
                <div>
                  <Label>Admin Position/Title</Label>
                  <Input
                    value={newUserData.position}
                    onChange={(e) => setNewUserData({ ...newUserData, position: e.target.value })}
                    placeholder="e.g., Church Secretary, Worship Leader"
                  />
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetNewUserForm(); }}>
                  Cancel
                </Button>
                <Button onClick={handleCreateUser} className="bg-amber-700 hover:bg-amber-800">
                  Create Account
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password for {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdatePassword} className="bg-amber-700 hover:bg-amber-800">
                Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Position Dialog */}
      <Dialog open={positionDialogOpen} onOpenChange={setPositionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Position for {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Admin Position/Title</Label>
              <Input
                value={newPosition}
                onChange={(e) => setNewPosition(e.target.value)}
                placeholder="e.g., Church Secretary, Worship Leader"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPositionDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdatePosition} className="bg-amber-700 hover:bg-amber-800">
                Update Position
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <Card><CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
        </CardContent></Card>
      ) : users.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Newsletter</TableHead>
                <TableHead>Login Method</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden">
                        {(user.profile_image || user.picture) ? (
                          <img src={user.profile_image || user.picture} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-5 w-5 text-stone-400" />
                        )}
                      </div>
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={roleColors[user.role]}>{user.role}</Badge>
                  </TableCell>
                  <TableCell>
                    {user.position ? (
                      <span className="text-stone-600">{user.position}</span>
                    ) : (
                      <span className="text-stone-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.newsletter_subscribed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Subscribed</span>
                    ) : (
                      <span className="text-xs text-stone-400">No</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.password_hash ? (
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        <Key className="h-3 w-3 mr-1" /> Password
                      </Badge>
                    ) : user.picture ? (
                      <Badge variant="outline" className="text-blue-600 border-blue-300">
                        Google
                      </Badge>
                    ) : (
                      <Badge variant="outline">Shared</Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {/* Role dropdown */}
                      {user.user_id !== currentUser?.user_id && (
                        <Select
                          value={user.role}
                          onValueChange={(value) => handleRoleChange(user.user_id, value)}
                        >
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="visitor">Visitor</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      
                      {/* Edit position */}
                      {user.role === 'admin' && (
                        <Button variant="ghost" size="sm" onClick={() => openPositionDialog(user)} title="Edit position">
                          <UserCog className="h-4 w-4" />
                        </Button>
                      )}
                      
                      {/* Change password */}
                      <Button variant="ghost" size="sm" onClick={() => openPasswordDialog(user)} title="Change password">
                        <Key className="h-4 w-4" />
                      </Button>
                      
                      {/* Delete user */}
                      {user.user_id !== currentUser?.user_id && (
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteUser(user.user_id)} title="Delete user">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      
                      {user.user_id === currentUser?.user_id && (
                        <span className="text-sm text-stone-500 px-2">You</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center">
          <Users className="h-12 w-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-800 mb-2">No Users</h3>
          <p className="text-stone-600">Users will appear here when they sign in.</p>
        </CardContent></Card>
      )}

      {/* Help Card */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-stone-800 mb-4">User Account Types</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-red-600" />
                <span className="font-medium text-red-800">Admin</span>
              </div>
              <p className="text-sm text-red-700">Full access to admin dashboard. Can manage sermons, members, settings, and other admins.</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">Member</span>
              </div>
              <p className="text-sm text-blue-700">Access to member directory. Can upload photos to galleries and submit prayer requests.</p>
            </div>
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-5 w-5 text-stone-600" />
                <span className="font-medium text-stone-800">Visitor</span>
              </div>
              <p className="text-sm text-stone-600">Basic access only. Can submit prayer requests and contact forms.</p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-amber-50 rounded-lg">
            <h4 className="font-medium text-amber-800 mb-1">Login Methods</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li><strong>Password:</strong> User has their own email + password (created via "Create Account")</li>
              <li><strong>Google:</strong> User signed in with Google OAuth</li>
              <li><strong>Shared:</strong> Using the shared admin password (legacy)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsers;
